<template>
  <input-popover v-model:open="showDropdown" :position="position" :offset="offset">
    <template #trigger>
      <div>
        <slot
          name="trigger"
          :label="formatter(selectedOption)"
          :show-dropdown="showDropdown"
          :search-term="searchTerm"
          :display-value="displayValue"
          :is-editing="isEditing"
          :on-search="handleSearch"
          :focus-input="focusSearchInput"
          :input-ref="
            (el: Element | ComponentPublicInstance | null) =>
              (searchInputRef = el as HTMLInputElement | null)
          "
        >
          <div
            class="flex cursor-pointer items-center justify-between rounded-xs border border-gray-800 p-1 text-sm text-gray-600"
            data-cy="defaultDropdownTrigger"
          >
            <input
              v-if="searchable"
              ref="searchInputRef"
              v-model="searchTerm"
              type="text"
              class="w-full bg-transparent outline-none"
              :placeholder="String(formatter(selectedOption)) || placeholder"
              data-cy="dropdownSearchInput"
              @click="showDropdown ? $event.stopPropagation() : null"
              @focus="showDropdown = true"
            />
            <span v-else>
              {{ formatter(selectedOption) }}
            </span>
            <icon-arrow :class="showDropdown ? '-rotate-90' : 'rotate-90'" />
          </div>
        </slot>
      </div>
    </template>
    <template #content>
      <div class="relative">
        <ul
          class="max-h-80 overflow-y-auto bg-white shadow-md"
          :class="{
            'p-2': styleOptionsWithDefault.menuPadded,
            'relative top-2': styleOptionsFromProps.menuGap,
          }"
        >
          <li
            v-for="optionItem in filteredOptions"
            :key="optionItem.id"
            @click="selectOption(optionItem.id)"
          >
            <slot name="option" v-bind="optionItem" :active="modelValue === optionItem.id">
              <div
                class="w-full cursor-pointer p-2 text-sm text-gray-600 hover:bg-blue-100"
                data-cy="defaultDropdownContent"
              >
                {{ formatter(optionItem) }}
              </div>
            </slot>
          </li>
          <li v-if="searchable && !filteredOptions.length" class="p-2 text-sm text-gray-400">
            {{ noResultsText }}
          </li>
        </ul>
      </div>
    </template>
  </input-popover>
</template>

<script lang="ts">
export type Option = {
  id: string | number;
};

export interface StyleOptions {
  menuPadded?: boolean;
  menuGap?: boolean;
}
</script>

<script setup lang="ts" generic="K extends Option">
import { ref, computed, watch, nextTick, type PropType, type ComponentPublicInstance } from "vue";
import InputPopover from "@/components/input/InputPopover.vue";
import { type VariationPlacement, type BasePlacement } from "@popperjs/core";
import IconArrow from "@/components/icons/IconArrow.vue";
import merge from "lodash/merge";

const props = defineProps({
  options: {
    type: Array as PropType<Readonly<K[]>>,
    required: true,
  },
  position: {
    type: String as PropType<VariationPlacement | BasePlacement>,
    default: "bottom-start",
  },
  modelValue: {
    type: [String, Number],
    default: "",
  },
  formatter: {
    type: Function as PropType<(option: K | undefined) => string | number>,
    default: (option: K | undefined) => option?.id ?? "",
  },
  offset: {
    type: Array as PropType<number[]>,
    default: () => [0, 0],
  },
  styleOptions: {
    type: Object as PropType<StyleOptions>,
    default: undefined,
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  noResultsText: {
    type: String,
    default: "No results found",
  },
});

const defaultStyles = {
  menuPadded: true,
  menuGap: false,
} as const;

const styleOptionsFromProps = { ...props.styleOptions };
const styleOptionsWithDefault = merge(props.styleOptions, defaultStyles);

const emit = defineEmits<{
  "update:modelValue": [id: string | number];
}>();

const showDropdown = ref(false);
const searchTerm = ref("");
const isEditing = ref(false);
const searchInputRef = ref<HTMLInputElement | null>(null);

const selectedOption = computed(() => {
  return props.options.find((option) => option.id === props.modelValue);
});

const displayValue = computed(() => {
  if (isEditing.value) {
    return searchTerm.value;
  }
  return selectedOption.value ? String(props.formatter(selectedOption.value)) : "";
});

const filteredOptions = computed(() => {
  if (!props.searchable || !searchTerm.value) {
    return props.options;
  }
  const term = searchTerm.value.toLowerCase();
  return props.options.filter((option) => {
    const label = String(props.formatter(option)).toLowerCase();
    return label.includes(term);
  });
});

const handleSearch = (value: string) => {
  isEditing.value = true;
  searchTerm.value = value;
};

const focusSearchInput = () => {
  nextTick(() => {
    searchInputRef.value?.focus();
  });
};

const selectOption = (id: string | number) => {
  emit("update:modelValue", id);
  showDropdown.value = false;
  searchTerm.value = "";
  isEditing.value = false;
};

watch(showDropdown, (isOpen) => {
  if (isOpen && props.searchable) {
    focusSearchInput();
  } else {
    searchTerm.value = "";
    isEditing.value = false;
  }
});
</script>
